import type { FC, ReactNode } from 'react'
import type { ArticleSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'

interface JournalCardProps {
    article: ArticleSummary
    className?: string
}

export const JournalCard: FC<JournalCardProps> = ({ article, className }): ReactNode => {
    return (
        <Link
            href={`/journal/${article.slug}`}
            className={cn('group/card flex w-full flex-col items-center gap-4', className)}
        >
            <div className='relative aspect-square w-full overflow-clip bg-muted'>
                {article.cover && (
                    <img
                        src={article.cover}
                        alt={article.coverAlt ?? ''}
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 size-full scale-105 object-cover transition-transform duration-slow ease-studio group-hover/card:scale-100 group-focus-visible/card:scale-100'
                    />
                )}

                <span
                    aria-hidden='true'
                    className='absolute bottom-3 right-[11px] flex translate-y-2 items-center gap-1.5 bg-background px-1.5 py-2 opacity-0 transition-[opacity,transform] duration-base ease-studio group-hover/card:translate-y-0 group-hover/card:opacity-100 group-focus-visible/card:translate-y-0 group-focus-visible/card:opacity-100'
                >
                    <span className='t-caps whitespace-pre text-[16px] text-foreground'>
                        Read
                    </span>

                    <ArrowUpRight className='size-3.5 text-foreground' strokeWidth={1.5} />
                </span>
            </div>

            <div className='flex w-full flex-col gap-3 text-foreground'>
                <p className='t-meta text-left'>
                    <time dateTime={article.publishedOn}>
                        {formatDate(article.publishedOn)}
                    </time>
                </p>

                <h3
                    data-mdlfy-cms={`articles:${article.id}:title`}
                    className='t-label text-left [text-wrap:pretty]'
                >
                    {article.title}
                </h3>
            </div>
        </Link>
    )
}
